// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The model, and the two decisions that are load-bearing.
 *
 * WHICH REPOSITORY. `onnx-community/depth-anything-v2-small` carries every
 * weight as a single file, has 59,008 downloads against its successor's 2,373,
 * and is the identifier transformers.js hard-codes as the default for the
 * depth-estimation task. Hugging Face flags it as superseded by
 * `depth-anything-v2-small-ONNX`, which splits every weight into external data
 * — two requests per weight, and its int8 build is 38.4 MB where this one's is
 * 27.3 MB. Staying here is the right call and the pin is a revision, not a
 * branch, so the bytes cannot change under us.
 *
 * WHICH WEIGHTS. This is the decision that could have shipped a broken tool.
 *
 * The obvious choice is `q4f16` at 19.1 MB — the smallest file, and what this
 * tool's own specification asked for. It is not shipped, because a quantised
 * graph that the WebGPU execution provider cannot run properly DOES NOT THROW.
 * It returns numbers. Wrong depth renders as a plausible-looking but incorrect
 * parallax, which is the one failure a person could not detect by looking at
 * the result. Reading the file's bytes shows q4f16 is built on the
 * `com.microsoft` contrib operator `MatMulNBits`, and two open transformers.js
 * issues (#1728, #1749) document exactly this class of silent wrong output for
 * q4/q8 weights on that provider. The only public deployment of this model —
 * the library author's own realtime demo — uses fp16 or fp32 and never q4f16.
 *
 * So: fp16 on a graphics adapter that advertises `shader-f16`, and the
 * standard int8 CPU build otherwise. `model_quantized.onnx` is a plain ORT
 * dynamic-quantisation graph (DynamicQuantizeLinear / ConvInteger /
 * MatMulInteger, no contrib operators) and is what the library itself picks for
 * WebAssembly, so the CPU tier is on a well-trodden path too.
 *
 * Every byte count below was measured against the CDN this build, and the
 * weights are NOT compressed on the wire — a one-megabyte ranged request
 * returns exactly one megabyte — so these are the real download sizes.
 */

export const MODEL_ID = 'onnx-community/depth-anything-v2-small';
export const MODEL_REVISION = '4472b7362082ad9968fee890ca0f1e5aca36b93d';

/** Apache-2.0, both the ONNX conversion and the base model. */
export const MODEL_LICENCE = 'Apache-2.0';

export const TIERS = {
  webgpu: {
    device: 'webgpu',
    dtype: 'fp16',
    file: 'onnx/model_fp16.onnx',
    bytes: 49_642_442,
    label: 'your graphics card',
  },
  wasm: {
    device: 'wasm',
    dtype: 'q8',
    file: 'onnx/model_quantized.onnx',
    bytes: 27_258_801,
    label: 'the processor',
  },
};

/**
 * The ONNX Runtime binaries, served from this origin.
 *
 * Not a rounding error on the download: the asyncify build is 23,567,050 bytes
 * on disk, and it is fetched on every path — including WebGPU, whose execution
 * provider is itself a WebAssembly build. Any figure quoted to a person as "the
 * download" has to include it.
 *
 * This is the GZIP size, measured against a live tool on GitHub Pages, because
 * that is what this tool's own host actually serves. The brotli figure that a
 * CDN would send is 4,732,131 bytes and quoting it here would understate the
 * download by more than a megabyte on the only host this page will ever run on.
 */
export const RUNTIME_BYTES_OVER_THE_WIRE = 5_862_143;
